import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { SortOrder, TranscriptSortField } from "./utils";

export type TranscriptsVariant = "page" | "embedded";

interface TranscriptsUIState {
	variant: TranscriptsVariant;
	selectedId: string | null;
	setSelectedId: (id: string | null) => void;
	search: string;
	setSearch: (search: string) => void;
	sortField: TranscriptSortField;
	setSortField: (field: TranscriptSortField) => void;
	sortOrder: SortOrder;
	setSortOrder: (order: SortOrder) => void;
	contextPanelOpen: boolean;
	setContextPanelOpen: (open: boolean) => void;
}

const TranscriptsContext = createContext<TranscriptsUIState | null>(null);

export function useTranscriptsUI(): TranscriptsUIState {
	const context = useContext(TranscriptsContext);
	if (!context)
		throw new Error("useTranscriptsUI must be used within a TranscriptsProvider");
	return context;
}

/**
 * UI-only state for a transcripts surface. Server data lives in react-query.
 * The full page mirrors the selected transcript to the URL (?t=...) for
 * refresh/deep-linking; the embedded candidate tab keeps selection local.
 */
export const TranscriptsProvider: React.FC<{ variant: TranscriptsVariant; children: React.ReactNode }> = ({
	variant,
	children,
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [localSelectedId, setLocalSelectedId] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [sortField, setSortField] = useState<TranscriptSortField>("date");
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
	// Details panel starts open on the desktop page; the embedded tab and
	// mobile render it as a Sheet, which should stay closed until toggled.
	const [contextPanelOpen, setContextPanelOpen] = useState(
		() =>
			variant === "page" &&
			typeof window !== "undefined" &&
			window.matchMedia("(min-width: 768px)").matches
	);

	const selectedId = variant === "page" ? searchParams.get("t") : localSelectedId;

	const setSelectedId = useCallback(
		(id: string | null) => {
			if (variant === "page") {
				setSearchParams(
					(previous) => {
						const next = new URLSearchParams(previous);
						if (id) next.set("t", id);
						else next.delete("t");
						return next;
					},
					{ replace: true }
				);
			} else {
				setLocalSelectedId(id);
			}
		},
		[variant, setSearchParams]
	);

	const value = useMemo(
		() => ({
			variant,
			selectedId,
			setSelectedId,
			search,
			setSearch,
			sortField,
			setSortField,
			sortOrder,
			setSortOrder,
			contextPanelOpen,
			setContextPanelOpen,
		}),
		[variant, selectedId, setSelectedId, search, sortField, sortOrder, contextPanelOpen]
	);

	return <TranscriptsContext.Provider value={value}>{children}</TranscriptsContext.Provider>;
};
